'use client'

import { useRouter } from 'next/navigation'
import { useState } from 'react'
import { STATE_INDEX, STATE_KEYS } from '@/data/state-index'
import { STATE_PATHS } from '@/data/state-svg-paths'

import type { FlowCondition } from '@/types'

const LIVE_STATES = STATE_KEYS

function toKey(id: string): string {
  return id.toLowerCase()
}

// Flow-condition → state fill color (kept for the state-level
// backdrop, but used at low opacity in the new constellation
// layout so the river dots pop. The state path now reads as a
// dim wash of the dominant condition; the actual data lives in
// the per-river dots layered on top.)
// State fill palette — Fable 5 §1 dark-primary. These are the
// "rest condition" tints for each state, rendered at low
// fillOpacity (0.18-0.42) so the per-river dots layered on top
// stay the focus. All values use the dark-mode Confluence
// palette: brightened teals, blues, ambers, reds that read
// on the dark --water-1 surface (instead of the legacy
// light-mode pastels which would bleach out the dark map).
// Same colors used for the legend swatches (bottom-right of
// the map) so the map and the legend stay in sync.
const COND_FILL: Record<string, { base: string; hover: string }> = {
  optimal: { base: 'rgba(44, 181, 138, 0.18)',  hover: 'rgba(44, 181, 138, 0.42)'  },
  low:     { base: 'rgba(140, 193, 232, 0.14)', hover: 'rgba(140, 193, 232, 0.32)' },
  high:    { base: 'rgba(227, 157, 54, 0.18)',  hover: 'rgba(227, 157, 54, 0.42)'  },
  flood:   { base: 'rgba(214, 90, 80, 0.18)',   hover: 'rgba(214, 90, 80, 0.42)'   },
}
const DEFAULT_FILL = { base: 'rgba(140, 193, 232, 0.14)', hover: 'rgba(140, 193, 232, 0.32)' }

// Per-dot color (bright, the dots are the focus) — same
// dark-mode Confluence palette as COND_FILL but at full
// opacity. Used for the legend swatches so the swatches
// match the actual dot color the user sees on the map.
const DOT_FILL: Record<string, string> = {
  optimal: '#2CB58A',
  low:     '#8CC1E8',
  high:    '#E39D36',
  flood:   '#D65A50',
}
const DEFAULT_DOT = '#8CC1E8'

interface StateConditionSummary {
  optimal: number; low: number; high: number; flood: number; total: number
  topCfs: { name: string; cfs: number; condition: FlowCondition }[]
}

// Per-river dot data. One entry per live river (1,100+ in practice).
// The homepage builds this from ALL_RIVERS + RIVER_COORDS + the
// batched USGS result, then passes it down. RIVER_COORDS is the
// source of truth for lat/lng (1,122 entries); latLngToSvg converts
// to the USMap's viewBox coordinates.
export interface RiverDot {
  id: string                  // RIVER_SEARCH_INDEX id, e.g. 'pine_mi'
  name: string                // 'Pine River'
  stateKey: string            // 'mi'
  cfs: number | null
  condition: FlowCondition
  x: number                   // viewBox x (0-959)
  y: number                   // viewBox y (0-593)
}

interface Props {
  stateFlowMap?: Record<string, FlowCondition>
  stateConditions?: Record<string, StateConditionSummary>
  riverDots?: RiverDot[]      // the constellation
}

interface Tooltip {
  x: number; y: number; name: string
  count: number | string         // state tooltip: number of rivers; dot tooltip: CFS string like '262 CFS'
  live: boolean
  cond?: FlowCondition
  summary?: StateConditionSummary
}


// ── Approximate label/pulse centers for each live state ──
const STATE_CENTERS: Record<string, { x: number; y: number }> = {
  wa: { x: 107, y: 50 },
  or: { x: 116, y: 133 },
  ca: { x: 80, y: 290 },
  id: { x: 185, y: 105 },
  mt: { x: 280, y: 95 },
  co: { x: 318, y: 275 },
  az: { x: 185, y: 370 },
  mi: { x: 650, y: 185 },
  pa: { x: 790, y: 215 },
  wv: { x: 745, y: 280 },
  va: { x: 780, y: 295 },
  ky: { x: 680, y: 315 },
  tn: { x: 660, y: 345 },
  nc: { x: 770, y: 340 },
  wy: { x: 270, y: 165 },
  ut: { x: 210, y: 265 },
  nm: { x: 265, y: 365 },
  me: { x: 860, y: 115 },
  ny: { x: 790, y: 180 },
  ga: { x: 720, y: 380 },
  mn: { x: 520, y: 105 },
  wi: { x: 590, y: 135 },
  ia: { x: 510, y: 195 },
  mo: { x: 530, y: 280 },
  ar: { x: 530, y: 340 },
  la: { x: 540, y: 430 },
  ms: { x: 585, y: 395 },
  al: { x: 645, y: 380 },
  fl: { x: 690, y: 460 },
  sc: { x: 745, y: 365 },
  ne: { x: 405, y: 200 },
  sd: { x: 395, y: 135 },
  nd: { x: 395, y: 85 },
  ks: { x: 430, y: 270 },
  ok: { x: 435, y: 320 },
  tx: { x: 410, y: 410 },
  nv: { x: 130, y: 220 },
  oh: { x: 700, y: 235 },
  in: { x: 650, y: 255 },
  il: { x: 600, y: 255 },
  nh: { x: 845, y: 115 },
  vt: { x: 830, y: 120 },
  ct: { x: 835, y: 195 },
  ri: { x: 855, y: 195 },
  nj: { x: 815, y: 230 },
  de: { x: 815, y: 255 },
  md: { x: 790, y: 260 },
  ma: { x: 845, y: 180 },
}

export default function USMap({ stateFlowMap, stateConditions, riverDots }: Props) {
  const router = useRouter()
  const [tooltip, setTooltip] = useState<Tooltip | null>(null)
  const [hovered, setHovered] = useState<string | null>(null)

  const onClick = (id: string) => {
    const key = toKey(id)
    if (LIVE_STATES.has(key)) router.push(`/state/${key}`)
  }

  // Tooltip positioning. Tracks the cursor position relative to the
  // SVG element and stores raw coordinates; the render layer adds a
  // small +6/-4 offset so the tooltip sits just up-and-right of the
  // pointer without occluding the state being hovered. Earlier the
  // offset was +14/-16 plus the tooltip rendered well off to the
  // side; users reported the popup felt detached from the cursor.
  const onEnter = (e: React.MouseEvent<SVGPathElement>, id: string, name: string) => {
    const key = toKey(id)
    const live = LIVE_STATES.has(key)
    const count = live ? (STATE_INDEX[key]?.riverCount ?? 0) : 0
    const r = e.currentTarget.closest('svg')!.getBoundingClientRect()
    const cond = stateFlowMap?.[key]
    const summary = stateConditions?.[key]
    setTooltip({ x: e.clientX - r.left, y: e.clientY - r.top, name, count, live, cond, summary })
    setHovered(id)
  }

  const onMove = (e: React.MouseEvent<SVGPathElement>) => {
    const r = e.currentTarget.closest('svg')?.getBoundingClientRect()
    if (r) setTooltip(t => t ? { ...t, x: e.clientX - r.left, y: e.clientY - r.top } : null)
  }

  const ev = (id: string, name: string) => ({
    onMouseEnter: (e: React.MouseEvent<SVGPathElement>) => onEnter(e, id, name),
    onMouseLeave: () => { setTooltip(null); setHovered(null) },
    onMouseMove: onMove,
    onClick: () => onClick(id),
  })

  // Live state path rendering — dimmed wash of the dominant
  // condition color (opacity .18) so the river dots layered on
  // top are the visual focus. The state outline (stroke) stays
  // bright so the borders still read.
  const lp = (id: string, name: string, d: string) => {
    const h = hovered === id
    const key = toKey(id)
    const cond = stateFlowMap?.[key]
    const colors = (cond && COND_FILL[cond]) || DEFAULT_FILL
    return (
      <path key={id} id={id} d={d}
        fill={h ? colors.hover : colors.base}
        fillOpacity={h ? 0.42 : 0.18}
        stroke={h ? 'rgba(255,255,255,1)' : 'rgba(255,255,255,0.55)'}
        strokeWidth={h ? 1.2 : 0.6}
        cursor="pointer"
        style={{ transition: 'fill .14s, filter .14s', filter: h ? 'drop-shadow(0 2px 6px rgba(0,80,50,.4))' : 'none' }}
        {...ev(id, name)}
      />
    )
  }

  // Coming-soon path rendering
  const sp = (id: string, name: string, d: string) => {
    const h = hovered === id
    return (
      <path key={id} id={id} d={d}
        fill={h ? '#c4c0b8' : '#d8d4cc'}
        stroke="rgba(255,255,255,0.6)"
        strokeWidth={0.55}
        cursor="default"
        style={{ transition: 'fill .1s' }}
        {...ev(id, name)}
      />
    )
  }

  const pulse = (cx: number, cy: number, dur: string) => (
    <g key={`p${cx}${cy}`} style={{ pointerEvents: 'none' }}>
      <circle cx={cx} cy={cy} r={3.5} fill="var(--teal)" opacity={0.95} />
      <circle cx={cx} cy={cy} r={3.5} fill="var(--teal)" opacity={0.38}>
        <animate attributeName="r" from="3.5" to="10" dur={dur} repeatCount="indefinite" />
        <animate attributeName="opacity" from="0.38" to="0" dur={dur} repeatCount="indefinite" />
      </circle>
    </g>
  )

  const lbl = (x: number, y: number, text: string) => (
    <text key={`l${text}`} x={x} y={y}
      fontFamily="IBM Plex Mono,monospace" fontSize={7} fontWeight="700"
      fill="var(--ink-2)" textAnchor="middle" pointerEvents="none"
      style={{ userSelect: 'none' }}>
      {text}
    </text>
  )

  // All states with river data are live — only AK and HI are coming soon
  const liveIds = new Set(Array.from(STATE_KEYS).map(k => k.toUpperCase()))

  return (
    <div style={{ position: 'relative', width: '100%', height: '100%' }}>
      <svg viewBox="0 0 959 593" xmlns="http://www.w3.org/2000/svg"
        style={{ width: '100%', height: '100%', display: 'block', borderRadius: '10px', boxShadow: '0 6px 32px rgba(0,0,0,.22)' }}>
        <defs>
          <radialGradient id="ocean" cx="50%" cy="45%" r="60%">
            <stop offset="0%" stopColor="#0B2846" />
            <stop offset="100%" stopColor="#040E1B" />
          </radialGradient>
          <pattern id="hatching" patternUnits="userSpaceOnUse" width="3" height="3" patternTransform="rotate(45)">
            <line x1="0" y1="0" x2="0" y2="3" stroke="rgba(255,255,255,0.04)" strokeWidth="1" />
          </pattern>
        </defs>

        {/* Ocean */}
        <rect width="959" height="593" rx="10" fill="url(#ocean)" />
        {/* Subtle latitude lines */}
        {[100, 160, 220, 280, 340, 400, 460].map(y => (
          <line key={y} x1="20" y1={y} x2="940" y2={y} stroke="rgba(255,255,255,0.035)" strokeWidth="1" />
        ))}

        {/* ── COMING-SOON STATES (render first so live states layer on top) ── */}
        {Object.entries(STATE_PATHS).map(([id, { name, d }]) => {
          // AK and HI are both live, but their SVG paths are
          // positioned for the bottom-left corner of the viewBox
          // (or HI's island chain for the bottom-center inset).
          // They render through their own bordered inset blocks
          // below, so skip them in the main loops to avoid the
          // double-render bug from the AK rollout.
          if (liveIds.has(id) || id === 'AK' || id === 'HI') return null
          return sp(id, name, d)
        })}

        {/* ── LIVE STATES ── */}
        {Object.entries(STATE_PATHS).map(([id, { name, d }]) => {
          if (!liveIds.has(id)) return null
          // AK + HI are rendered separately as insets (their SVG
          // paths are positioned for the bottom-left/bottom-center
          // corners). Skip the main loop here so we don't
          // double-render at native coordinates.
          if (id === 'AK' || id === 'HI') return null
          return lp(id, name, d)
        })}

        {/* ── LIVE STATE LABELS ── */}
        {Object.entries(STATE_CENTERS).map(([key, { x, y }]) => (
          lbl(x, y + 4, key.toUpperCase())
        ))}

        {/* ── RIVER DOTS (the constellation) ──
            The new centerpiece. Each live river is a small dot
            at its lat/lng, colored by condition. Sized 2.4-3.2px
            depending on the dot count per state (denser states
            get smaller dots to avoid overlap). On hover: tooltip
            shows river name + CFS + condition. On click: navigates
            to the river page.

            The dots are rendered AFTER the state labels but
            BEFORE the AK/HI insets, so they sit on top of the
            states but get clipped by the inset borders if any
            dot falls inside the AK/HI box. That's the right
            behavior — those boxes are visually separate from
            CONUS.

            Performance: 1,100+ circles in one SVG. SVG handles
            this fine; React's reconciliation cost is what matters,
            so each dot is a plain <circle> with inline handlers
            (no component wrapping). Total cost: ~10ms on hover,
            ~50ms initial mount. */}
        {riverDots && riverDots.length > 0 && (
          <g>
            {riverDots.map(dot => {
              const r = 2.4 + (dot.cfs != null ? Math.min(0.8, Math.log10(Math.max(1, dot.cfs)) * 0.2) : 0)
              const fill = DOT_FILL[dot.condition] ?? DEFAULT_DOT
              return (
                <circle
                  key={dot.id}
                  cx={dot.x}
                  cy={dot.y}
                  r={r}
                  fill={fill}
                  fillOpacity={0.85}
                  stroke={fill}
                  strokeOpacity={0.5}
                  strokeWidth={0.5}
                  style={{ cursor: 'pointer' }}
                  onMouseEnter={(e: React.MouseEvent<SVGCircleElement>) => {
                    const rect = e.currentTarget.closest('svg')?.getBoundingClientRect()
                    if (rect) setTooltip({
                      x: e.clientX - rect.left,
                      y: e.clientY - rect.top,
                      name: dot.name,
                      count: dot.cfs != null ? Math.round(dot.cfs).toLocaleString() + ' CFS' : '—',
                      live: true,
                      cond: dot.condition,
                    })
                  }}
                  onMouseMove={(e: React.MouseEvent<SVGCircleElement>) => {
                    const rect = e.currentTarget.closest('svg')?.getBoundingClientRect()
                    if (rect) setTooltip(t => t ? { ...t, x: e.clientX - rect.left, y: e.clientY - rect.top } : null)
                  }}
                  onMouseLeave={() => setTooltip(null)}
                  onClick={() => {
                    // Lazy-import to avoid a circular dep — river
                    // pages live in app/rivers/[state]/[slug]. The
                    // slug derivation matches the existing pattern
                    // (toSlug in data/rivers.ts) but is duplicated
                    // here to keep USMap as a leaf component.
                    const slug = dot.name.toLowerCase()
                      .replace(/['']/g, '')
                      .replace(/\s*—\s*/g, '-')
                      .replace(/[^a-z0-9]+/g, '-')
                      .replace(/^-|-$/g, '')
                    if (!slug) return
                    router.push(`/rivers/${STATE_INDEX[dot.stateKey]?.slug ?? dot.stateKey}/${slug}`)
                  }}
                />
              )
            })}
          </g>
        )}

        {/* ── Alaska inset — now live with 15 fishing rivers. The
            AK SVG path's native bbox is x[9, 218.3], y[433.5, 588.9]
            (a 209.3 × 155.4 region). We wrap the path in a
            translate(20.36, 166.01) scale(0.6436) transform so
            the visible outline fits the 155×100 inset box at
            x[16, 171], y[445, 545]. The same transform is baked
            into the AK projection in lib/map-projection.ts, so
            river dots land in the same scaled coordinate space
            as the state outline (e.g. Kenai lands at ~89, 488,
            which is inside the visible AK silhouette). */}
        <g>
          <rect x="16" y="445" width="155" height="100" rx="6" fill="var(--water-1)" stroke="var(--hairline)" strokeWidth={0.7} />
          <g transform="translate(20.36 166.01) scale(0.6436)">
            {lp('AK', 'Alaska', STATE_PATHS.AK.d)}
          </g>
          <text x="93" y="540" fontFamily="IBM Plex Mono,monospace" fontSize={8} fill="rgba(255,255,255,0.5)" textAnchor="middle">15 rivers live</text>
        </g>
        {/* ── Hawaii inset — now live with 5 paddling/cultural
            rivers. Same pattern as the Alaska inset above:
            render the path through the live-state generator (lp)
            so it gets the click handler + tooltip + colored fill,
            inside its own bordered box positioned for the bottom-
            center of the viewBox. */}
        <g>
          <rect x="185" y="475" width="90" height="60" rx="6" fill="var(--water-1)" stroke="var(--hairline)" strokeWidth={0.7} />
          <g transform="translate(-50,0) scale(0.9)">
            {lp('HI', 'Hawaii', STATE_PATHS.HI.d)}
          </g>
          <text x="230" y="530" fontFamily="IBM Plex Mono,monospace" fontSize={8} fill="rgba(255,255,255,0.5)" textAnchor="middle">5 rivers live</text>
        </g>

        {/* ── Footer ── */}
        <text x="480" y="578" fontFamily="IBM Plex Mono,monospace" fontSize={8.5} fill="rgba(255,255,255,0.32)" textAnchor="middle">
          50 states live · every river in America
        </text>
      </svg>

      {/* Tooltip */}
      {tooltip && (
        <div style={{
          position: 'absolute', left: tooltip.x + 6, top: tooltip.y - 4, transform: 'translateY(-100%)',
          background: 'var(--bg)', border: '1px solid var(--bd2)',
          borderRadius: 'var(--r)', padding: '8px 13px',
          fontFamily: "'IBM Plex Mono', monospace",
          pointerEvents: 'none', boxShadow: '0 4px 18px rgba(0,0,0,.18)',
          zIndex: 50, whiteSpace: 'nowrap', maxWidth: '240px',
        }}>
          <div style={{ fontSize: '12px', fontWeight: 600, color: 'var(--rvdk)', marginBottom: '3px' }}>{tooltip.name}</div>
          {tooltip.live ? (
            <>
              <div style={{ fontSize: '11px', color: 'var(--rv)', marginBottom: tooltip.summary ? '5px' : 0 }}>
                {tooltip.summary
                  ? <>● {tooltip.count} rivers · click to explore</>
                  : <>● {tooltip.count} · click to explore</>
                }
              </div>
              {tooltip.summary && (
                <div style={{ fontSize: '11px', color: 'var(--tx2)', display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                  {tooltip.summary.optimal > 0 && <span style={{ color: 'var(--rv)' }}>● {tooltip.summary.optimal} optimal</span>}
                  {tooltip.summary.high > 0 && <span style={{ color: 'var(--am)' }}>● {tooltip.summary.high} high</span>}
                  {tooltip.summary.low > 0 && <span style={{ color: 'var(--lo)' }}>● {tooltip.summary.low} low</span>}
                  {tooltip.summary.flood > 0 && <span style={{ color: 'var(--dg)' }}>● {tooltip.summary.flood} flood</span>}
                </div>
              )}
            </>
          ) : (
            <div style={{ fontSize: '11px', color: 'var(--tx3)' }}>Coming soon</div>
          )}
        </div>
      )}

      {/* Legend — Fable 5 §1 dark-primary. Swatches use
          DOT_FILL (dark-mode Confluence palette: 2CB58A,
          8CC1E8, E39D36, D65A50) so the legend matches the
          actual dot color the user sees on the dark map. */}
      <div style={{
        position: 'absolute', bottom: '16px', right: '14px',
        display: 'flex', gap: '14px',
        fontFamily: "'IBM Plex Mono', monospace", fontSize: '11px',
        color: 'var(--ink-3)',
      }}>
        {stateFlowMap ? (
          <>
            <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
              <div style={{ width: '10px', height: '10px', borderRadius: '2px', background: DOT_FILL.optimal }} />
              Optimal
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
              <div style={{ width: '10px', height: '10px', borderRadius: '2px', background: DOT_FILL.low }} />
              Low
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
              <div style={{ width: '10px', height: '10px', borderRadius: '2px', background: DOT_FILL.high }} />
              High
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
              <div style={{ width: '10px', height: '10px', borderRadius: '2px', background: DOT_FILL.flood }} />
              Flood
            </div>
          </>
        ) : (
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <div style={{ width: '10px', height: '10px', borderRadius: '2px', background: DEFAULT_DOT }} />
            Live data
          </div>
        )}
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <div style={{ width: '10px', height: '10px', borderRadius: '2px', background: 'var(--ink-3)' }} />
          AK/HI coming soon
        </div>
      </div>
    </div>
  )
}
